'use client';

import { useRef, useState, type CSSProperties } from 'react';

import { ArrowLeft, ArrowRight, Pause, Play, X } from 'lucide-react';

import { Dialog, DialogClose, DialogContent, DialogTitle } from '@kit/ui/dialog';

import type { GalleryBlock } from '~/(tenant)/_lib/schema/landing-config.schema';

import { landingImageUrl, pickLocalized, type LandingRenderContext } from './landing-context';
import styles from './gallery-marquee.module.css';

export function GalleryMarquee({ block, ctx }: { block: GalleryBlock; ctx: LandingRenderContext }) {
  const opener = useRef<HTMLButtonElement>(null);
  const swipe = useRef<number | null>(null);
  const [paused, setPaused] = useState(false);
  const [viewer, setViewer] = useState<{ index: number | null; portal?: HTMLElement; theme: CSSProperties }>({ index: null, theme: {} });
  const arabic = ctx.locale.startsWith('ar');
  const title = pickLocalized(block.title, block.title_ar, ctx.locale) || (arabic ? 'صور الاستوديو' : 'Studio photos');
  const count = block.images.length;
  if (!count) return null;
  const repeated = Array.from({ length: Math.ceil(10 / count) }, () => block.images).flat();
  const selected = viewer.index === null ? undefined : block.images[viewer.index % count];
  const move = (step: number) => setViewer((current) => ({ ...current, index: ((current.index ?? 0) + step + count) % count }));

  return (
    <section className={styles.section} aria-label={title} data-gallery-layout="marquee">
      <div className={styles.viewport} dir="ltr">
        <div className={styles.track} style={{ '--marquee-duration': `${repeated.length * 5}s` } as CSSProperties} data-paused={paused || viewer.index !== null || undefined}>
          {[...repeated, ...repeated].map((image, index) => (
            <button
              key={index}
              type="button"
              className={styles.thumbnail}
              tabIndex={index < count ? 0 : -1}
              aria-hidden={index >= count || undefined}
              aria-label={index < count ? `${arabic ? 'عرض' : 'View'} ${image.alt || `${arabic ? 'الصورة' : 'photo'} ${index + 1}`}` : undefined}
              aria-haspopup={index < count ? 'dialog' : undefined}
              data-gallery-original={index < count || undefined}
              onClick={(event) => {
                const trigger = event.currentTarget;
                const document = trigger.ownerDocument;
                opener.current = index < count ? trigger : trigger.parentElement?.querySelector<HTMLButtonElement>(`[data-gallery-original]:nth-child(${index % count + 1})`) ?? trigger;
                const computed = document.defaultView?.getComputedStyle(trigger);
                const theme = computed ? {
                  ...Object.fromEntries(['--primary', '--primary-foreground', '--background', '--foreground', '--border', '--ring', '--landing-canvas', '--landing-ink'].map((name) => [name, computed.getPropertyValue(name)])),
                  fontFamily: computed.fontFamily,
                } : {};
                setViewer({ index: index % count, portal: document.body, theme });
              }}
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src={landingImageUrl(ctx, image.imageKey)} alt="" draggable={false} loading="lazy" style={{ objectPosition: image.focalPoint }} />
            </button>
          ))}
        </div>
      </div>
      <button type="button" className={styles.pause} aria-label={paused ? (arabic ? 'استئناف حركة الصور' : 'Resume photo ribbon') : (arabic ? 'إيقاف حركة الصور' : 'Pause photo ribbon')} aria-pressed={paused} onClick={() => setPaused((current) => !current)}>
        {paused ? <Play size={14} aria-hidden /> : <Pause size={14} aria-hidden />}
      </button>
      <Dialog open={viewer.index !== null} onOpenChange={(open) => { if (!open) setViewer((current) => ({ ...current, index: null })); }}>
        <DialogContent
          portalContainer={viewer.portal}
          className={styles.dialog}
          style={viewer.theme}
          dir={arabic ? 'rtl' : 'ltr'}
          hideCloseButton
          aria-label={title}
          aria-describedby={undefined}
          onCloseAutoFocus={(event) => { event.preventDefault(); opener.current?.focus({ preventScroll: true }); }}
          onKeyDown={(event) => {
            if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
            event.preventDefault();
            move(event.key === 'ArrowRight' ? 1 : -1);
          }}
        >
          <DialogTitle className="sr-only">{title}</DialogTitle>
          <DialogClose className={styles.close} aria-label={arabic ? 'إغلاق' : 'Close'}><X size={16} aria-hidden /></DialogClose>
          <div className={styles.photo}
            onPointerDown={(event) => {
              if (!event.isPrimary || event.button !== 0) return;
              swipe.current = event.clientX;
              event.currentTarget.setPointerCapture(event.pointerId);
            }}
            onPointerUp={(event) => {
              if (swipe.current !== null && Math.abs(event.clientX - swipe.current) > 40) move(event.clientX < swipe.current ? 1 : -1);
              swipe.current = null;
            }}
            onPointerCancel={() => { swipe.current = null; }}
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            {selected && <img src={landingImageUrl(ctx, selected.imageKey)} alt={selected.alt ?? ''} draggable={false} />}
          </div>
          {count > 1 && <div className={styles.navigation} dir="ltr">
            <button type="button" className={styles.previous} aria-label={arabic ? 'الصورة السابقة' : 'Previous photo'} onClick={() => move(-1)}><ArrowLeft size={20} aria-hidden /></button>
            <button type="button" className={styles.next} aria-label={arabic ? 'الصورة التالية' : 'Next photo'} onClick={() => move(1)}><ArrowRight size={20} aria-hidden /></button>
          </div>}
          <div className={styles.caption} aria-live="polite" aria-atomic="true">
            <p>{selected?.alt}</p>
            <p dir="ltr">{(viewer.index ?? 0) + 1} / {count}</p>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
